import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import { Segment } from 'tea-component';
import PageAccess from '@gp/components/PageAccess';
import GPSelectMultiple from '@gp/components/SelectMultiple';
import DataTime from '@gp/components/DataTime';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import Overview from './components/Overview';
import Forecast from './components/Forecast';
import { useRequest, useMemoizedFn } from 'ahooks';
import { fetchStudioOverallOverviewPageData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import {
  SEGMENT_OPT,
  SEGMENT_OVERVIEW,
  SEGMENT_FIVE_YEAR_FORECAST,
  STUDIO_DATA_TYPE_LANGUAGE_OPT,
} from './constants';
import { DESCRIPTION_C } from '@gp/constants/noPermissionTips';
import { getArraySort } from '@gp/utils/common';
import {
  formatStudioOptions,
  getStudioDataTypeDefaultValue,
} from '@gp/pages/StudioDashboard/StudioOverall/utils';
import { LANGUAGE_EN } from '@home/_config/config';

import './index.scss';

interface IOptBase {
  text: string;
  textCN: string;
  value: string;
  disabled: boolean;
  children?: IChildrenOpt[];
}
interface IChildrenOpt extends IOptBase {
  bubbleContent?: string;
  bubbleContentCN?: string;
}

// const overallValue = 'all';
/* const defaultOpt: IOptBase[] = [
  {
    children: [
      {
        text: 'Studio A',
        textCN: '中文Studio A',
        value: 'up_split_studio_a',
        disabled: false,
      },
      {
        text: 'Studio B',
        textCN: '中文Studio B',
        value: 'up_split_studio_b',
        disabled: false,
      },
      {
        text: 'Studio C',
        textCN: '中文Studio C',
        value: 'up_split_studio_c',
        disabled: false,
      },
    ],
    text: 'Up',
    textCN: '中文Up',
    value: 'up',
    disabled: false,
  },
  {
    children: [
      {
        text: 'Studio A',
        textCN: '中文Studio A',
        value: 'base_split_studio_a',
        disabled: false,
      },
      {
        text: 'Studio B',
        textCN: '中文Studio B',
        value: 'base_split_studio_b',
        disabled: false,
      },
      {
        text: 'Studio C',
        textCN: '中文Studio C',
        value: 'base_split_studio_c',
        disabled: false,
      },
      {
        text: 'Studio D',
        textCN: '中文Studio D',
        value: 'base_split_studio_d',
        disabled: false,
      },
    ],
    text: 'Base',
    textCN: '中文Base',
    value: 'base',
    disabled: false,
  },
]; */
/* const radioOptions = [
  {
    value: overallValue,
    text: 'All',
    textCN: '全部',
  },
]; */

const Finance: React.FC<IStudioOverallFinanceProps> = () => {
  const [segmentValue, setSegmentValue] = useState<'1' | '2'>(SEGMENT_OVERVIEW);
  const [selectBO, setSelectBO] = useState<string[]>([]);
  const [lastSelectBO, setLastSelectBO] = useState<string[]>([]);
  // 5-Year Forecast页面
  const [updateTime, setUpdateTime] = useState<string>('');
  const [selectValue, setSelectValue] = useState<{
    markets: string[];
    regions: string[];
    type: 'market' | 'region';
  }>({
    markets: [],
    regions: [],
    type: 'region',
  });
  const [studioOptions, setStudioOptions] = useState<IOptBase[]>([]);
  // 判断studio筛选器data type旁边的select all按钮是否隐藏
  const [isHideFilterSelectAllButton, setIsHideFilterSelectAllButton] = useState<boolean>(true);

  const [filterBOList, setFilterBOList] = useState<any>({});
  const [filterBOLoading, setFilterBOLoading] = useState(false);

  // bo筛选器数据
  // const {
  //   loading: filterBOLoading,
  //   data: filterBOList,
  //   run: filterBORun,
  // } = useRequest(
  //   fetchStudioOverallOverviewPageData,
  //   {
  //     manual: true,
  //   },
  // );

  const filterBORun = async () => {
    try {
      setFilterBOLoading(true);
      const res = await fetchStudioOverallOverviewPageData({ page: 'finance_5y' });
      setFilterBOList(res);
    } catch (exception) {
      console.error(exception);
    } finally {
      setFilterBOLoading(false);
    }
  };

  const boOptions = useMemo(() => {
    const options = [];
    const boList = filterBOList?.management_team;
    if (Array.isArray(boList) && boList?.length) {
      // 先排序
      let isIncludeOthersItem = false;
      const filterOthersBOlist = boList?.filter((t: string) => {
        if (t?.toLowerCase() === 'others') {
          isIncludeOthersItem = true;
        }
        return t?.toLowerCase() !== 'others';
      });
      const tempSortBOList = getArraySort(filterOthersBOlist, 'string', 'asc');
      const sortBOList = isIncludeOthersItem ? [...tempSortBOList, 'Others'] : tempSortBOList;
      for (let i = 0; i < sortBOList?.length; i++) {
        const t = sortBOList?.[i];
        options.push({
          text: t,
          value: t,
          groupKey: 'separated',
        });
      }
    }

    return options;
  }, [filterBOLoading]);

  const handleBOFilterChange = useCallback(
    (v: string[]) => {
      // 重复全选的效果不请求接口
      if (v?.length === filterBOList?.management_team?.length && !lastSelectBO?.length) return;
      // 当length是全选时，也设置成[]
      if (v?.length === filterBOList?.management_team?.length) {
        setSelectBO([]);
        setLastSelectBO([]);
        return;
      }

      const newSelectBO = v ? [...v] : [];
      setSelectBO(newSelectBO);
      setLastSelectBO(newSelectBO);
    },
    [filterBOList?.management_team?.length, lastSelectBO],
  );

  const handleCloseReginSelect = useCallback((result: any) => {
    setSelectValue(result);
  }, []);

  const handleChangeReginSelect = useCallback(
    (result: any, entryStudioOptions?: IOptBase[]) => {
      const markets = result?.markets;
      const realStudioOptions = entryStudioOptions || studioOptions;
      const flag =
        !Array.isArray(markets) || !markets?.length; /* || markets?.[0] === overallValue */
      setIsHideFilterSelectAllButton(flag);
      if (Array.isArray(markets) && Array.isArray(realStudioOptions)) {
        const newStudioOptions = realStudioOptions?.map((k) => {
          const children = k?.children;
          const newOptionItem = { ...k };
          if (Array.isArray(children)) {
            const newChildren = [...children]?.map((n) => {
              const currentValue = n?.value;
              const currentArr = currentValue?.split('_split_');
              const currentDataType = currentArr?.[0];
              const currentStudio = currentArr?.[1];
              let isOtherItemSelected = false; // 当前这一项是否在其它data type中被选中
              let selectedValue = ''; // 被选中项的studio value
              for (let i = 0, l = markets?.length; i < l; i++) {
                const t = markets?.[i];
                if (t && typeof t === 'string') {
                  const arr = t?.split('_split_');
                  const dataType = arr?.[0];
                  const studio = arr?.[1];
                  // 在所有选中的studio中，某一项，data type和当前项不一样，但studio和当前项一样，说明当前项被其它地方选了
                  if (dataType !== currentDataType && currentStudio === studio) {
                    isOtherItemSelected = true;
                    selectedValue = t;
                    break;
                  }
                }
              }
              let bubbleContent = '';
              let bubbleContentCN = '';
              // 找到被选中项studio的data type中英文名称
              if (selectedValue) {
                const dataTypeV = selectedValue?.split('_split_')?.[0];
                for (let i = 0, l = realStudioOptions?.length; i < l; i++) {
                  const t = realStudioOptions?.[i];
                  if (t?.value === dataTypeV) {
                    bubbleContent = `Already selected in ${t?.text} data type.`;
                    bubbleContentCN = `已在${t?.textCN}数据类型中选择。`;
                    break;
                  }
                }
              }
              const newItem = {
                ...n,
                // 被选了的studio需要禁用
                disabled: isOtherItemSelected,
                bubbleContent,
                bubbleContentCN,
              };

              return newItem;
            });
            const isCurrentItemAllChildrenSelected = newChildren?.every((t) => t?.disabled);

            return {
              ...newOptionItem,
              children: newChildren,
              // data type下面全部studio是禁用时，左侧对应data type选项禁用掉
              disabled: isCurrentItemAllChildrenSelected,
              // bubbleContent: '',
              // bubbleContentCN: '',
            };
          }
          return newOptionItem;
        });
        setStudioOptions(newStudioOptions);
      }
    },
    [studioOptions],
  );

  useEffect(() => {
    filterBORun({ page: 'finance_5y' });
  }, []);

  // 判断studio筛选器data type旁边的select all按钮是否隐藏
  useEffect(() => {
    const markets = selectValue?.markets;
    const flag = !Array.isArray(markets) || !markets?.length;
    setIsHideFilterSelectAllButton(flag);
  }, [selectValue]);

  // 设置默认值及data type筛选器选项
  useEffect(() => {
    if (filterBOLoading) return;
    const studioOptionsTmp = formatStudioOptions(filterBOList?.data_type);
    const defalutSelectValue = getStudioDataTypeDefaultValue(filterBOList?.data_type);
    setSelectValue(defalutSelectValue);
    handleChangeReginSelect(defalutSelectValue, studioOptionsTmp);
  }, [filterBOLoading]);

  return (
    // 暂时没有权限这个页签直接没有，这里不需要做判断
    <PageAccess isAccess={true} description={DESCRIPTION_C}>
      <main className="dbg-studio-overall-finance">
        <section className="dbg-studio-overall-finance-filter">
          <Segment
            className="dbg-studio-overall-finance-segment segment-light"
            value={segmentValue}
            options={SEGMENT_OPT}
            onChange={(value: '1' | '2') => {
              setSelectBO([]);
              setLastSelectBO([]);
              setSegmentValue(value);
              // 目前配置一样，不用重新请求
              // filterBORun({ page: 'finance' });
            }}
          />
          <GPSelectMultiple
            className="dbg-studio-overall-finance-bo-selector"
            label="Business Owner"
            searchable
            disabled={filterBOLoading}
            options={boOptions}
            value={selectBO}
            onChange={handleBOFilterChange}
          />
          {segmentValue === SEGMENT_FIVE_YEAR_FORECAST && (
            <>
              <NewRegionSelect
                className="dbg-studio-overall-finance-year-forecast-studio-filter"
                boxClassName={classnames('dbg-region-dropdown-box', {
                  'dbg-studio-filter-hide-select-all-btn': isHideFilterSelectAllButton,
                })}
                showRadioOptionsTips={false}
                value={selectValue}
                options={studioOptions}
                locale={LANGUAGE_EN}
                language={STUDIO_DATA_TYPE_LANGUAGE_OPT}
                closeOnScroll={true}
                // radioOptions={radioOptions}
                onCloseReginSelect={handleCloseReginSelect}
                onChange={handleChangeReginSelect}
              />
              <DataTime
                className="dbg-studio-overall-finance-year-forecast-update-time"
                dateType="Month"
                date={updateTime}
              />
            </>
          )}
        </section>

        {/* Overview */}
        {segmentValue === SEGMENT_OVERVIEW && (
          <Overview selectBO={selectBO} filterBOLoading={filterBOLoading} />
        )}

        {/* 5-Year Forecast */}
        {segmentValue === SEGMENT_FIVE_YEAR_FORECAST && (
          <Forecast
            selectBO={selectBO}
            selectValue={selectValue}
            filterBOLoading={filterBOLoading}
            setUpdateTime={setUpdateTime}
          />
        )}
      </main>
    </PageAccess>
  );
};

export default memo(Finance);
